import axios from 'axios';
import { ref } from 'vue';

import { getOrCreateFraudSid } from '@/composables/api/useFraudSession';

const INITIAL_LOAD_TIMEOUT_MS = 30000;
/** Idle period required after the last in-flight request before dismissing the loader */
const INITIAL_LOAD_SETTLE_MS = 600;

const SESSION_TOKEN_TTL_MS = 115 * 60 * 1000;
const SESSION_TOKEN_BACKFILL_MS = SESSION_TOKEN_TTL_MS - (10 * 60 * 1000);
const SESSION_TOKEN_LAST_SEEN_KEY = 'sessiontoken_last_seen';
const PERSISTENT_TOKEN_KEYS = ['xsrftoken', 'sessiontoken'];

export const pendingRequestCount = ref(0);
export const hasSeenApiRequest = ref(false);
export const isInitialLoadComplete = ref(false);
export const hasRouteMounted = ref(false);

let appMounted = false;
let settleTimer = null;

const clearSettleTimer = () => {
  if (settleTimer) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
};

export const markInitialLoadComplete = () => {
  isInitialLoadComplete.value = true;
  clearSettleTimer();
};

const isBootstrapReady = () => appMounted && hasRouteMounted.value;

const scheduleSettleTimer = () => {
  if (settleTimer || isInitialLoadComplete.value) return;

  settleTimer = setTimeout(() => {
    settleTimer = null;
    if (isInitialLoadComplete.value || pendingRequestCount.value > 0 || !isBootstrapReady()) {
      return;
    }
    markInitialLoadComplete();
  }, INITIAL_LOAD_SETTLE_MS);
};

const tryCompleteInitialLoad = () => {
  if (isInitialLoadComplete.value || !isBootstrapReady()) return;

  if (pendingRequestCount.value > 0) {
    clearSettleTimer();
    return;
  }

  scheduleSettleTimer();
};

export const signalAppMounted = () => {
  appMounted = true;
  tryCompleteInitialLoad();
};

export const signalRouteMounted = () => {
  hasRouteMounted.value = true;
  tryCompleteInitialLoad();
};

const trackRequestStart = () => {
  hasSeenApiRequest.value = true;
  pendingRequestCount.value += 1;
  clearSettleTimer();
};

const trackRequestEnd = () => {
  pendingRequestCount.value = Math.max(0, pendingRequestCount.value - 1);
  tryCompleteInitialLoad();
};

setTimeout(() => {
  if (!isInitialLoadComplete.value) {
    markInitialLoadComplete();
  }
}, INITIAL_LOAD_TIMEOUT_MS);

const appUrl = window.location.origin;
const urls = import.meta.env.VITE_LOCAL_HTTP_URLS
  ? import.meta.env.VITE_LOCAL_HTTP_URLS.split(',')
  : ['http://localhost:3000', 'http://10.0.2.2:3000'];

const baseUrl = urls.includes(appUrl)
  ? import.meta.env.VITE_APP_API_ENDPOINT
  : `${appUrl}/spa/`;

const apiClient = axios.create({
  baseURL: baseUrl,
  // withCredentials: true,
  headers: {
    Accept: 'application/json, text/plain, */*',
    'Content-type': 'application/json',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
  },
});

const readPersistentToken = (key) => {
  const persisted = localStorage.getItem(key);
  if (persisted !== null) {
    return persisted;
  }

  const legacy = sessionStorage.getItem(key);
  if (legacy !== null) {
    localStorage.setItem(key, legacy);
    sessionStorage.removeItem(key);
    return legacy;
  }

  return null;
};

const clearPersistentTokens = () => {
  PERSISTENT_TOKEN_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem(SESSION_TOKEN_LAST_SEEN_KEY);
};

const touchSessionTokenActivity = () => {
  if (localStorage.getItem('sessiontoken') !== null) {
    localStorage.setItem(SESSION_TOKEN_LAST_SEEN_KEY, String(Date.now()));
  }
};

const enforceSessionTokenTtl = () => {
  if (localStorage.getItem('sessiontoken') === null) {
    return;
  }

  let lastSeen = Number(localStorage.getItem(SESSION_TOKEN_LAST_SEEN_KEY));

  if (!lastSeen) {
    lastSeen = Date.now() - SESSION_TOKEN_BACKFILL_MS;
    localStorage.setItem(SESSION_TOKEN_LAST_SEEN_KEY, String(lastSeen));
  }

  if (Date.now() - lastSeen > SESSION_TOKEN_TTL_MS) {
    clearPersistentTokens();
  }
};

apiClient.interceptors.request.use((config) => {
  trackRequestStart();
  enforceSessionTokenTtl();

  const xsrftoken = readPersistentToken('xsrftoken');
  const sessiontoken = readPersistentToken('sessiontoken');

  if (xsrftoken) {
    config.headers.set('X-XSRF-TOKEN', xsrftoken);
  }

  if (sessiontoken) {
    config.headers.set('Session-Token', sessiontoken);
  }

  config.headers.set('X-Fraud-Sid', getOrCreateFraudSid());

  return config;
});

apiClient.interceptors.response.use(
  (res) => {
    trackRequestEnd();

    if (res.headers.get('resettokens')) {
      clearPersistentTokens();
    }

    const xsrf = res.headers.get('xsrftoken');
    if (xsrf) {
      localStorage.setItem('xsrftoken', xsrf);
    }

    const session = res.headers.get('sessiontoken');
    if (session) {
      localStorage.setItem('sessiontoken', session);
    }

    touchSessionTokenActivity();

    return res;
  },
  async (error) => {
    trackRequestEnd();

    const { config, response } = error;

    if (response && response?.status === 419 && config && !config.csrfRetry) {
      config.csrfRetry = true;

      localStorage.removeItem('xsrftoken');
      sessionStorage.removeItem('xsrftoken');

      try {
        await apiClient.get('/csrf-cookie');
        return await apiClient.request(config);
      } catch (retryError) {
        clearPersistentTokens();
        return Promise.reject(retryError);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
